import React from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { auth, firestore } from '../lib';
import { User } from '../models';

type TUserContext = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  firebaseUser: FirebaseUser;
  setFirebaseUser: React.Dispatch<React.SetStateAction<FirebaseUser>>;
};

export const UserContext = React.createContext<TUserContext>({
  user: undefined,
  setUser: undefined,
  firebaseUser: undefined,
  setFirebaseUser: undefined,
});

interface UserContextProviderProps {
  children: React.ReactNode;
}

export function UserContextProvider({ children }: UserContextProviderProps) {
  const [user, setUser] = React.useState<User>();
  const [firebaseUser, setFirebaseUser] = React.useState<FirebaseUser>();
  const router = useRouter();
  React.useEffect(function syncAuth() {
    const unsubscribe = onAuthStateChanged(auth, async (_user) => {
      if (_user) {
        setFirebaseUser(_user);
        const snapshot = await getDoc(doc(firestore, 'users', _user.uid));
        // const snapshot = await getDoc(
        //   doc(firestore, 'users', 'lAPMhEqtB7WkI3ksHVe5zIAi48z2')
        // );
        if (snapshot.exists()) {
          const snapshotDataUser = {
            ...snapshot.data(),
          } as User;
          setUser(snapshotDataUser);
          try {
            posthog?.identify(snapshotDataUser.id, {
              ...snapshotDataUser,
              password: undefined,
            });
          } catch (err) {
            posthog?.captureException(err);
          }
          Sentry.setUser({
            email: snapshotDataUser.email,
            username: `${snapshotDataUser.firstName} ${snapshotDataUser.lastName}`,
          });
        }
      } else {
        setUser(undefined);
        posthog?.reset();
        Sentry.setUser(null);
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);
  const value = React.useMemo(
    () => ({ user, setUser, firebaseUser, setFirebaseUser }),
    [user, firebaseUser]
  );
  const onSignOut = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      await auth.signOut();
      posthog?.reset();
      await router.push('/');
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <UserContext.Provider value={value}>
      <div className="">
        {user ? (
          <div className="fixed top-4 right-4 p-3 z-50 shadow bg-white rounded-sm border border-magenta">
            <p className="text-gray-600 text-sm">
              Hello {user?.firstName ?? ''} you're register as{' '}
              <span className="text-mid-blue">{user?.privilege}</span>
            </p>
            <button
              type="button"
              className="text-gray-600 text-sm my-1"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>
        ) : null}

        {children}
      </div>
    </UserContext.Provider>
  );
}
